class EmergencyTriageQuestions extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    const root = this;

    const AAV_DATA = {

      species: [
        { id: "dog",   em: "🐶", label: "Dog" },
        { id: "cat",   em: "🐱", label: "Cat" },
        { id: "other", em: "🐾", label: "Other" }
      ],

      /* Any red flag = RED, straight through. iso = also raises the isolation banner. */
      redflags: [
        { id: "rf-breath",   text: "Struggling to breathe, choking, or open-mouth breathing in a cat" },
        { id: "rf-gums",     text: "Pale, white, blue or grey gums or tongue" },
        { id: "rf-collapse", text: "Collapsed, unresponsive, or unable to stand" },
        { id: "rf-seizure",  text: "Seizing now, more than one seizure today, or a seizure lasting over 5 minutes" },
        { id: "rf-bleed",    text: "Bleeding that will not stop, or blood from mouth, nose or any wound in volume" },
        { id: "rf-gdv",      text: "Trying to vomit with nothing coming up, plus a swollen or tight belly" },
        { id: "rf-urine",    text: "Straining to urinate with little or nothing coming out, especially a male cat" },
        { id: "rf-toxin",    text: "Ate or may have eaten a poison, bait, toxic food or human medication" },
        { id: "rf-trauma",   text: "Major trauma: hit by a car, a fall from height, a dog attack, or a penetrating wound" },
        { id: "rf-heat",     text: "Suspected heatstroke: hot day or exertion, heavy panting, weakness or collapse" },
        { id: "rf-snake",    text: "Possible snake bite or other envenomation" },
        { id: "rf-birth",    text: "Birthing trouble: strong straining with no puppy or kitten, or hours between births" },
        { id: "rf-pain",     text: "Severe pain: crying out, unable to settle, or will not be touched" },
        { id: "rf-anaph",    text: "Sudden facial swelling or hives with vomiting, weakness or breathing changes" },
        { id: "rf-eye",      text: "Eye out of its socket, sudden blindness, or a chemical splashed in the eye" },
        { id: "rf-hypo",     text: "A diabetic on insulin that is wobbly, flat or refusing food" },
        { id: "rf-ment",     text: "Suddenly dull, confused, unresponsive or not moving (altered mentation)" },
        { id: "rf-ate",      text: "Cat suddenly unable to use its back legs, crying out, legs cold (possible arterial clot)" },
        { id: "rf-env",      text: "Drowning or near-drowning, electrocution, smoke inhalation, or burns" }
      ],

      /* Question types:
         chk  - checkbox. level: "red" | "amber" | null. iso: true adds isolation banner.
         sel  - select. opts: [{v, label, level}]
         txt  - free text
         note - standing guidance shown inside the set (level info/red/amber) */
      complaints: [
        {
          id: "c-breath", em: "🫁", label: "Breathing problems",
          seo: "Laboured breathing, noisy breathing and open-mouth breathing in cats are among the most time-critical presentations an emergency clinic sees. Dyspnoea has a short differential list (airway obstruction, pleural space disease, heart failure, pneumonia, asthma in cats) and almost all of it deteriorates with stress, which is why these patients skip the waiting room and go straight to oxygen.",
          qs: [
            { id: "q-br-now",    t: "chk", text: "Working hard to breathe right now (effort, noise, or belly heaving)", level: "red", flag: "Active breathing difficulty" },
            { id: "q-br-cat",    t: "chk", text: "Cat breathing with its mouth open at any point", level: "red", flag: "Open-mouth breathing in a cat" },
            { id: "q-br-blue",   t: "chk", text: "Tongue or gums looked blue, grey or very pale", level: "red", flag: "Cyanosis or pallor reported" },
            { id: "q-br-choke",  t: "chk", text: "May have something stuck in the throat (was chewing a toy, ball or bone)", level: "red", flag: "Possible airway foreign body" },
            { id: "q-br-onset",  t: "sel", text: "When did it start?", opts: [
                { v: "", label: "Select" },
                { v: "sudden", label: "Suddenly, within the last few hours", level: "amber", flag: "Sudden-onset breathing change" },
                { v: "today", label: "Today" },
                { v: "days", label: "A few days" },
                { v: "weeks", label: "Weeks or longer" } ] },
            { id: "q-br-heart",  t: "chk", text: "Known heart disease or a heart murmur", level: "amber", flag: "Known cardiac history with respiratory signs" },
            { id: "q-br-cough",  t: "chk", text: "Coughing (note how long and whether anything comes up)" },
            { id: "q-br-kc",     t: "chk", text: "Honking cough but otherwise bright, after recent boarding, daycare or the groomer", iso: true, flag: "Possible kennel cough: isolate from other dogs" },
            { id: "q-br-rest",   t: "sel", text: "Is it there even at rest?", opts: [
                { v: "", label: "Select" },
                { v: "rest", label: "Yes, even resting", level: "amber", flag: "Respiratory signs at rest" },
                { v: "exercise", label: "Only with exercise or excitement" } ] },
            { id: "note-br", t: "note", level: "red", text: "Any patient with genuine breathing difficulty is RED regardless of the answers above. Minimal handling, no restraint, straight through to oxygen." }
          ]
        },
        {
          id: "c-vomit", em: "🤮", label: "Vomiting or diarrhoea",
          seo: "Most vomiting dogs and cats are stable, but three patterns are genuine emergencies: unproductive retching with a swelling belly (bloat or GDV, minutes to hours matter), significant blood in vomit or stool, and the flat, unvaccinated puppy or kitten where parvovirus is on the list and the patient needs isolating on arrival.",
          qs: [
            { id: "q-vd-gdv",    t: "chk", text: "Trying to vomit but nothing comes up, or the belly looks swollen or tight", level: "red", flag: "Unproductive retching with distension: possible GDV" },
            { id: "q-vd-count",  t: "sel", text: "How many vomits in the last 24 hours?", opts: [
                { v: "", label: "Select" },
                { v: "1-2", label: "Once or twice" },
                { v: "3-5", label: "Three to five times" },
                { v: "many", label: "More than five, or ongoing", level: "amber", flag: "Frequent or ongoing vomiting" } ] },
            { id: "q-vd-blood",  t: "sel", text: "Any blood in the vomit or stool?", opts: [
                { v: "", label: "Select" },
                { v: "none", label: "No blood seen" },
                { v: "streak", label: "Streaks of fresh blood", level: "amber", flag: "Blood streaks in vomit or stool" },
                { v: "lots", label: "A lot of blood, or vomit like coffee grounds", level: "red", flag: "Significant GI bleeding reported" },
                { v: "melena", label: "Black, tarry stool", level: "amber", flag: "Melaena reported" } ] },
            { id: "q-vd-water",  t: "chk", text: "Cannot keep even water down", level: "amber", flag: "Unable to hold down water" },
            { id: "q-vd-flat",   t: "chk", text: "Quiet, weak or flat between episodes", level: "amber", flag: "Lethargic between GI episodes" },
            { id: "q-vd-young",  t: "chk", text: "Puppy or kitten, or not fully vaccinated, with vomiting or diarrhoea", level: "amber", iso: true, flag: "Unvaccinated young animal with GI signs: parvo risk, isolate" },
            { id: "q-vd-fb",     t: "chk", text: "Could have swallowed a toy, bone, corn cob, sock, string or similar", level: "amber", flag: "Possible GI foreign body" },
            { id: "q-vd-toxin",  t: "chk", text: "Could have gotten into food, rubbish, compost or anything toxic", level: "amber", flag: "Possible dietary indiscretion or toxin" },
            { id: "q-vd-dur",    t: "txt", text: "How long has it been going on, and when was the last episode?" }
          ]
        },
        {
          id: "c-toxin", em: "☠️", label: "Toxin or swallowed object",
          seo: "Poisonings are won or lost on timing: within roughly two hours of ingestion the vet can often decontaminate before the toxin is absorbed, so what was eaten and when are the two questions that matter most. Chocolate, rat bait, snail bait, grapes and raisins, xylitol, human medications, lilies for cats and dog flea products applied to cats make up most of the caseload.",
          qs: [
            { id: "q-tx-what",   t: "sel", text: "What did they get into?", opts: [
                { v: "", label: "Select" },
                { v: "choc", label: "Chocolate", level: "amber", flag: "Chocolate ingestion" },
                { v: "ratbait", label: "Rat or mouse bait", level: "red", flag: "Rodenticide ingestion" },
                { v: "snail", label: "Snail or slug bait", level: "red", flag: "Snail bait ingestion" },
                { v: "humanmed", label: "Human medication", level: "red", flag: "Human medication ingestion" },
                { v: "petmed", label: "Overdose of a pet medication", level: "red", flag: "Pet medication overdose" },
                { v: "xylitol", label: "Sugar-free gum or lollies (xylitol)", level: "red", flag: "Possible xylitol ingestion" },
                { v: "grapes", label: "Grapes, raisins or sultanas", level: "red", flag: "Grape or raisin ingestion" },
                { v: "lily", label: "Lily (any part, cats)", level: "red", flag: "Lily exposure in a cat" },
                { v: "paracat", label: "Paracetamol and the pet is a cat", level: "red", flag: "Paracetamol in a cat" },
                { v: "permethrin", label: "Dog flea product on a cat", level: "red", flag: "Permethrin exposure in a cat" },
                { v: "onion", label: "Onion, garlic, leek or chives", level: "amber", flag: "Allium ingestion" },
                { v: "thc", label: "Cannabis or edibles", level: "amber", flag: "THC exposure" },
                { v: "antifreeze", label: "Antifreeze or coolant", level: "red", flag: "Possible ethylene glycol ingestion" },
                { v: "cleaning", label: "Cleaning product or chemical", level: "amber", flag: "Household chemical exposure" },
                { v: "object", label: "Non-food object (toy, sock, string, bone)", level: "amber", flag: "Foreign body ingestion" },
                { v: "unsure", label: "Not sure or something else", level: "amber", flag: "Unidentified possible toxin" } ] },
            { id: "q-tx-when",   t: "sel", text: "When?", opts: [
                { v: "", label: "Select" },
                { v: "u1", label: "Under an hour ago", level: "red", flag: "Recent ingestion: decontamination window" },
                { v: "1-2", label: "One to two hours ago", level: "red", flag: "Ingestion within 2 hours: decontamination window" },
                { v: "2-6", label: "Two to six hours ago", level: "amber", flag: "Ingestion 2 to 6 hours ago" },
                { v: "o6", label: "More than six hours ago" },
                { v: "unk", label: "Unknown", level: "amber", flag: "Unknown ingestion time" } ] },
            { id: "q-tx-sx",     t: "chk", text: "Already showing any symptoms (vomiting, wobbliness, tremors, drooling)", level: "red", flag: "Symptomatic toxin exposure" },
            { id: "q-tx-amount", t: "txt", text: "Roughly how much, and the pet's weight if known?" },
            { id: "q-tx-pack",   t: "chk", text: "Owner has the packaging, label or a photo of it" },
            { id: "note-tx", t: "note", level: "info", text: "Ask the owner to keep the packaging with them. Never advise inducing vomiting at home or in reception; that is the vet's call." }
          ]
        },
        {
          id: "c-collapse", em: "😵", label: "Collapse or weakness",
          seo: "A collapsed dog or cat has a short and serious differential list: internal bleeding, heart rhythm problems, heatstroke, low blood sugar in diabetics, and seizures that were not witnessed. Even a pet that seems fully recovered has earned a same-day vet exam, and one that is still down, pale or distended is seen immediately.",
          qs: [
            { id: "q-co-now",    t: "chk", text: "Still down, unresponsive, or unable to stand now", level: "red", flag: "Currently collapsed or unable to stand" },
            { id: "q-co-lost",   t: "chk", text: "Lost consciousness at any point", level: "red", flag: "Loss of consciousness reported" },
            { id: "q-co-gums",   t: "chk", text: "Gums looked pale or white at the time", level: "red", flag: "Pallor with collapse" },
            { id: "q-co-belly",  t: "chk", text: "Belly looks swollen or distended", level: "red", flag: "Collapse with abdominal distension" },
            { id: "q-co-heat",   t: "chk", text: "Happened during or after heat or heavy exercise", level: "red", flag: "Possible heatstroke or exertional collapse" },
            { id: "q-co-diab",   t: "chk", text: "Diabetic, and insulin was given today", level: "red", flag: "Possible insulin-induced hypoglycaemia" },
            { id: "q-co-recover",t: "sel", text: "How are they now?", opts: [
                { v: "", label: "Select" },
                { v: "normal", label: "Back to completely normal" },
                { v: "quiet", label: "Up, but quiet or wobbly", level: "amber", flag: "Incomplete recovery after collapse" },
                { v: "down", label: "Still down", level: "red", flag: "Still recumbent" } ] },
            { id: "q-co-first",  t: "sel", text: "Has this happened before?", opts: [
                { v: "", label: "Select" },
                { v: "first", label: "First time", level: "amber", flag: "First collapse episode" },
                { v: "before", label: "Has happened before" } ] },
            { id: "q-co-desc",   t: "txt", text: "What did it look like? (paddling, stiffening, went floppy, how long)" }
          ]
        },
        {
          id: "c-seizure", em: "⚡", label: "Seizures or tremors",
          seo: "A single short seizure in a known epileptic can usually wait for its vet; an active seizure, a cluster of seizures in one day, or any seizure over five minutes cannot, because prolonged seizing overheats the body and injures the brain. Toxins (snail bait, chocolate, cannabis, mouldy food) are a common cause the owner will not volunteer unless asked.",
          qs: [
            { id: "q-sz-now",    t: "chk", text: "Seizing right now", level: "red", flag: "Active seizure" },
            { id: "q-sz-count",  t: "sel", text: "How many seizures in the last 24 hours?", opts: [
                { v: "", label: "Select" },
                { v: "one", label: "One" },
                { v: "two", label: "Two or more", level: "red", flag: "Cluster seizures" } ] },
            { id: "q-sz-long",   t: "chk", text: "A seizure lasted more than 5 minutes", level: "red", flag: "Prolonged seizure over 5 minutes" },
            { id: "q-sz-norecover", t: "chk", text: "Not back to normal 30 minutes after the seizure ended", level: "amber", flag: "Prolonged post-ictal phase" },
            { id: "q-sz-first",  t: "chk", text: "First ever seizure", level: "amber", flag: "First seizure" },
            { id: "q-sz-toxin",  t: "chk", text: "Could have gotten into snail bait, chocolate, cannabis, mouldy food or medications", level: "red", flag: "Possible toxic cause of seizures or tremors" },
            { id: "q-sz-known",  t: "chk", text: "Known epileptic on medication (note the drug and any missed doses)" },
            { id: "q-sz-desc",   t: "txt", text: "Describe it: how long, what it looked like, time of the last one" }
          ]
        },
        {
          id: "c-trauma", em: "🚗", label: "Trauma or wounds",
          seo: "The mechanism of injury outranks how the patient looks: dogs hit by cars and cats that fall from height can walk in bright with a ruptured bladder, bleeding spleen or bruised lungs, and bite wounds are icebergs, with small punctures over crushed tissue underneath. That is why major trauma keeps its red flag even when the pet seems fine.",
          qs: [
            { id: "q-tr-mech",   t: "sel", text: "What happened?", opts: [
                { v: "", label: "Select" },
                { v: "hbc", label: "Hit by a car or bike", level: "red", flag: "Hit by car: internal injury until excluded" },
                { v: "fall", label: "Fell from height (window, balcony, arms)", level: "red", flag: "Fall from height" },
                { v: "fight", label: "Attacked by another animal", level: "amber", flag: "Bite wounds: deep damage often hidden" },
                { v: "cut", label: "Cut or graze" },
                { v: "unknown", label: "Found injured, cause unknown", level: "amber", flag: "Unknown mechanism of injury" } ] },
            { id: "q-tr-bleed",  t: "chk", text: "Still actively bleeding", level: "red", flag: "Active bleeding" },
            { id: "q-tr-breath", t: "chk", text: "Breathing looks different since the injury", level: "red", flag: "Respiratory change after trauma" },
            { id: "q-tr-walk",   t: "sel", text: "Can they walk?", opts: [
                { v: "", label: "Select" },
                { v: "normal", label: "Walking normally" },
                { v: "limp", label: "Limping", level: "amber", flag: "Lameness after trauma" },
                { v: "drag", label: "Dragging or unable to use the back legs", level: "red", flag: "Possible spinal injury" },
                { v: "cant", label: "Cannot get up", level: "red", flag: "Recumbent after trauma" } ] },
            { id: "q-tr-pen",    t: "chk", text: "Wound over the chest or belly, or anything penetrating", level: "red", flag: "Penetrating or body-cavity wound" },
            { id: "q-tr-chest",  t: "chk", text: "Bite wounds over the chest, belly or neck, even if small", level: "amber", flag: "Bite wounds over a body cavity" },
            { id: "q-tr-when",   t: "txt", text: "When did it happen, and what first aid has been given?" },
            { id: "note-tr", t: "note", level: "amber", text: "Hit-by-car and fall patients can look bright with serious internal injuries. Mechanism keeps its flag even when the pet looks fine. Handle painful patients cautiously; ask the vet before muzzling anything with breathing or facial problems." }
          ]
        },
        {
          id: "c-urine", em: "💧", label: "Urinary problems",
          seo: "A male cat straining in the litter tray with little or nothing coming out is a possible urethral obstruction, one of the true hour-by-hour emergencies: a blocked bladder pushes potassium high enough to stop the heart. Blood in the urine or frequent small trips without systemic signs is urgent but usually not immediately life-threatening.",
          qs: [
            { id: "q-ur-block",  t: "chk", text: "Straining with little or nothing coming out", level: "red", flag: "Possible urinary obstruction" },
            { id: "q-ur-male",   t: "chk", text: "Male cat with any urinary signs", level: "red", flag: "Male cat with urinary signs: obstruction until excluded" },
            { id: "q-ur-last",   t: "sel", text: "When did they last pass a normal amount of urine?", opts: [
                { v: "", label: "Select" },
                { v: "today", label: "Today" },
                { v: "12-24", label: "12 to 24 hours ago", level: "amber", flag: "Reduced urine output over 12 hours" },
                { v: "o24", label: "Over 24 hours ago", level: "red", flag: "No normal urination for over 24 hours" },
                { v: "unk", label: "Owner unsure" } ] },
            { id: "q-ur-blood",  t: "chk", text: "Blood in the urine", level: "amber", flag: "Haematuria" },
            { id: "q-ur-sick",   t: "chk", text: "Also vomiting, flat or off food", level: "red", flag: "Systemically unwell with urinary signs" },
            { id: "q-ur-behave", t: "chk", text: "Crying in the litter tray, licking the back end, or frequent small trips" },
            { id: "q-ur-hx",     t: "txt", text: "Any previous urinary problems or blockages?" }
          ]
        },
        {
          id: "c-appetite", em: "🍽️", label: "Not eating or lethargic",
          seo: "How worrying inappetence is depends on the species and the clock: a dog skipping one meal is rarely urgent, a cat off food for more than a day risks hepatic lipidosis (especially if overweight), and a rabbit, guinea pig or ferret not eating for even half a day is an emergency because their guts and blood sugar fail fast.",
          qs: [
            { id: "q-ap-dur",    t: "sel", text: "How long off food?", opts: [
                { v: "", label: "Select" },
                { v: "u24", label: "Under 24 hours" },
                { v: "24-48", label: "24 to 48 hours", level: "amber", flag: "Anorexia over 24 hours" },
                { v: "o48", label: "Over 48 hours", level: "amber", flag: "Anorexia over 48 hours" } ] },
            { id: "q-ap-catfat", t: "chk", text: "Overweight cat that has stopped eating", level: "amber", flag: "Anorexic overweight cat: hepatic lipidosis risk" },
            { id: "q-ap-small",  t: "chk", text: "Rabbit, guinea pig or ferret not eating for 12 hours or more", level: "red", flag: "Anorexic small herbivore or ferret: GI stasis risk" },
            { id: "q-ap-young",  t: "chk", text: "Puppy or kitten under 6 months refusing food", level: "amber", flag: "Anorexic juvenile: hypoglycaemia risk" },
            { id: "q-ap-drink",  t: "sel", text: "Drinking?", opts: [
                { v: "", label: "Select" },
                { v: "normal", label: "Normally" },
                { v: "more", label: "More than usual", level: "amber", flag: "Polydipsia with inappetence" },
                { v: "less", label: "Less than usual" },
                { v: "none", label: "Not drinking at all", level: "amber", flag: "Not drinking" } ] },
            { id: "q-ap-vd",     t: "chk", text: "Also vomiting or diarrhoea (switch to that question set too)" },
            { id: "q-ap-flat",   t: "sel", text: "How flat are they?", opts: [
                { v: "", label: "Select" },
                { v: "quiet", label: "Quieter than normal" },
                { v: "verydull", label: "Very dull, barely interested in anything", level: "amber", flag: "Marked lethargy" },
                { v: "cantrise", label: "Reluctant or unable to get up", level: "red", flag: "Too weak to rise" } ] },
            { id: "q-ap-wl",     t: "chk", text: "Noticeable weight loss recently" },
            { id: "q-ap-desc",   t: "txt", text: "Anything else changed? (behaviour, hiding, breathing, toileting)" }
          ]
        },
        {
          id: "c-eye", em: "👁️", label: "Eye problems",
          seo: "Eyes fail fast and quietly: a prolapsed globe, sudden blindness, a chemical splash or a bulging painful eye (possible glaucoma) can cost the eye within hours, and a squinting, tearing eye usually means a painful corneal ulcer that needs same-day care. Eye problems are never a wait-and-see triage.",
          qs: [
            { id: "q-ey-prolapse", t: "chk", text: "Eye pushed out of its socket", level: "red", flag: "Globe prolapse" },
            { id: "q-ey-blind",  t: "chk", text: "Suddenly bumping into things or appears blind", level: "red", flag: "Acute vision loss" },
            { id: "q-ey-chem",   t: "chk", text: "Chemical, cleaning product or plant sap in the eye", level: "red", flag: "Chemical eye injury: needs immediate flushing" },
            { id: "q-ey-bulge",  t: "chk", text: "Eye looks bigger, bulging or cloudy compared with the other", level: "red", flag: "Possible glaucoma or globe swelling" },
            { id: "q-ey-pain",   t: "chk", text: "Eye held shut, tearing, or pawing at it", level: "amber", flag: "Painful eye: possible ulcer or foreign body" },
            { id: "q-ey-red",    t: "chk", text: "Red, weepy or discharging but open and comfortable" },
            { id: "q-ey-onset",  t: "txt", text: "When did it start, and was there any known knock or scratch?" },
            { id: "note-ey", t: "note", level: "info", text: "Eyes are unforgiving: a painful or changed eye is never a wait-and-see. Do not let owners apply old eye drops from home." }
          ]
        },
        {
          id: "c-lame", em: "🦴", label: "Lameness or back pain",
          seo: "Most limps can wait a day; two cannot. A dog dragging its back legs may have a spinal cord problem such as a slipped disc (IVDD), where hours change the odds of walking again, particularly in dachshunds and other long-backed breeds. An open fracture or a limb at a strange angle also needs immediate care and pain relief.",
          qs: [
            { id: "q-lm-drag",   t: "chk", text: "Dragging the back legs or unable to use them", level: "red", flag: "Possible spinal cord problem" },
            { id: "q-lm-ate",    t: "chk", text: "Cat with sudden hind leg weakness or paralysis, crying out, legs cool to touch", level: "red", flag: "Possible aortic thromboembolism in a cat" },
            { id: "q-lm-open",   t: "chk", text: "Bone visible, limb at a strange angle, or an open wound over the injury", level: "red", flag: "Open or obviously unstable fracture" },
            { id: "q-lm-nwb",    t: "sel", text: "Weight bearing?", opts: [
                { v: "", label: "Select" },
                { v: "partial", label: "Limping but using the leg" },
                { v: "none", label: "Not putting the leg down at all", level: "amber", flag: "Non-weight-bearing lameness" } ] },
            { id: "q-lm-back",   t: "chk", text: "Cries when picked up or touched over the back, or reluctant to move the neck", level: "amber", flag: "Possible spinal pain" },
            { id: "q-lm-breed",  t: "chk", text: "Long-backed breed (dachshund, corgi, basset, shih tzu) with sudden back pain or wobbliness", level: "amber", flag: "IVDD-prone breed with acute spinal signs" },
            { id: "q-lm-swell",  t: "chk", text: "Hot, swollen joint or limb", level: "amber", flag: "Hot swollen limb" },
            { id: "q-lm-trauma", t: "chk", text: "Known trauma caused it (switch to the trauma set too)" },
            { id: "q-lm-desc",   t: "txt", text: "Which leg, since when, better or worse?" }
          ]
        },
        {
          id: "c-allergy", em: "🐝", label: "Swelling or allergic reaction",
          seo: "A puffy face or hives after a sting looks dramatic and is usually treatable, but allergic reactions can escalate to anaphylaxis within minutes: any breathing change, vomiting or weakness after a sting, bite or new medication moves the patient to the front of the queue, and even comfortable swellings are rechecked every few minutes.",
          qs: [
            { id: "q-al-breath", t: "chk", text: "Any change in breathing or noisy breathing since the swelling started", level: "red", flag: "Airway involvement with allergic signs" },
            { id: "q-al-anaph",  t: "chk", text: "Vomiting, diarrhoea, weakness or collapse since the sting, bite or new drug", level: "red", flag: "Possible anaphylaxis" },
            { id: "q-al-face",   t: "chk", text: "Facial or muzzle swelling, or puffy eyes", level: "amber", flag: "Facial swelling: can progress" },
            { id: "q-al-hives",  t: "chk", text: "Hives or lumpy raised skin over the body", level: "amber", flag: "Urticaria: can progress" },
            { id: "q-al-trigger",t: "sel", text: "Known or likely trigger?", opts: [
                { v: "", label: "Select" },
                { v: "sting", label: "Insect sting or bite" },
                { v: "vacc", label: "Vaccine or medication today", level: "amber", flag: "Possible reaction to today's vaccine or drug" },
                { v: "food", label: "New food or treat" },
                { v: "unknown", label: "Unknown" } ] },
            { id: "q-al-when",   t: "txt", text: "When did it start, and is it spreading?" },
            { id: "note-al", t: "note", level: "amber", text: "Allergic reactions can escalate over minutes. Even a comfortable-looking facial swelling is rechecked every 5 to 10 minutes while waiting." }
          ]
        },
        {
          id: "c-birth", em: "🍼", label: "Birthing trouble",
          seo: "Dystocia risks the mother and every unborn puppy or kitten at once. The working thresholds: strong straining for 20 to 30 minutes with nothing delivered, more than two hours between babies with more expected, green or black discharge before the first arrival, or a visibly stuck puppy or kitten all mean the vet is needed now, not after another hour of waiting at home.",
          qs: [
            { id: "q-bi-strain", t: "chk", text: "Strong straining for over 20 to 30 minutes with no puppy or kitten", level: "red", flag: "Active unproductive straining" },
            { id: "q-bi-gap",    t: "chk", text: "More than 2 hours between births with more expected", level: "red", flag: "Prolonged interval between births" },
            { id: "q-bi-disc",   t: "chk", text: "Green or black discharge before the first baby has arrived", level: "red", flag: "Abnormal discharge before first delivery" },
            { id: "q-bi-stuck",  t: "chk", text: "A puppy or kitten visibly stuck", level: "red", flag: "Fetus lodged in the canal" },
            { id: "q-bi-flat",   t: "chk", text: "Mum weak, flat or trembling", level: "red", flag: "Maternal compromise" },
            { id: "q-bi-over",   t: "chk", text: "Past the due date (over 65 to 70 days from mating) with no labour", level: "amber", flag: "Prolonged gestation" },
            { id: "q-bi-count",  t: "txt", text: "How many born so far, how are they, and when did labour start?" },
            { id: "q-bi-first",  t: "chk", text: "First litter" }
          ]
        },
        {
          id: "c-other", em: "📋", label: "Something else",
          seo: "For anything that does not fit a set, the same principles hold: how long, how fast it is changing, whether the pet is still eating, drinking and bright, and a spoken re-run of the red-flag screen. A problem that is progressing quickly earns a higher category than the same problem that has been stable for a week.",
          qs: [
            { id: "q-ot-desc",   t: "txt", text: "Describe the problem in the owner's words" },
            { id: "q-ot-dur",    t: "txt", text: "How long has it been going on?" },
            { id: "q-ot-worse",  t: "sel", text: "Getting better or worse?", opts: [
                { v: "", label: "Select" },
                { v: "worse", label: "Worse, or changing quickly", level: "amber", flag: "Rapidly progressing problem" },
                { v: "same", label: "About the same" },
                { v: "better", label: "Improving" } ] },
            { id: "q-ot-eat",    t: "chk", text: "Eating and drinking normally" },
            { id: "q-ot-bright", t: "sel", text: "Demeanour?", opts: [
                { v: "", label: "Select" },
                { v: "bright", label: "Bright and normal" },
                { v: "quiet", label: "Quiet", level: "amber", flag: "Quiet or subdued demeanour" },
                { v: "flat", label: "Flat or weak", level: "red", flag: "Flat or weak on presentation" } ] },
            { id: "note-ot", t: "note", level: "info", text: "Re-run the red-flag screen out loud for anything that does not fit a set: breathing, gums, ability to stand, toxins, pain." }
          ]
        }
      ],

      categories: [
        { id: "red",    name: "RED",    title: "Immediate",  when: "Now", rank: 0,
          sub: "Straight through to the vet or treatment area",
          care: [
            "Take the patient through now; alert the vet as you move",
            "Minimal handling and stress, especially cats and breathing cases",
            "Start consent and estimate paperwork with the owner once the pet is through",
            "Finish the history and this sheet while the team works"
          ] },
        { id: "orange", name: "ORANGE", title: "Urgent",     when: "Vet within 15 min", rank: 1,
          sub: "Vet within 15 minutes; recheck every 5 to 10 minutes",
          care: [
            "Keep the patient where staff can see it, not in a far corner",
            "Recheck every 5 to 10 minutes; any change is re-triaged from the top",
            "Nothing to eat or drink until the vet advises",
            "Cats stay in carriers, up high, facing away from dogs"
          ] },
        { id: "yellow", name: "YELLOW", title: "Semi-urgent", when: "Vet within 30-60 min", rank: 2,
          sub: "Vet within 30 to 60 minutes; recheck every 15 to 30 minutes",
          care: [
            "Recheck every 15 to 30 minutes and note the time of each check",
            "Ask the owner to flag any change immediately",
            "Nothing to eat or drink until the vet advises",
            "Cats stay in carriers, up high, facing away from dogs"
          ] },
        { id: "green",  name: "GREEN",  title: "Routine",    when: "Standard queue", rank: 3,
          sub: "Standard queue; re-triage if anything changes or the wait passes an hour",
          care: [
            "Re-triage if anything changes, or if the wait passes an hour",
            "Nothing to eat or drink until the vet advises, in case sedation is needed",
            "Cats stay in carriers, up high, facing away from dogs"
          ] }
      ],

      /* Vitals reference ranges for triage: adult, at rest. Applied for dog and cat only.
         Normal bands and red thresholds follow standard emergency triage protocol values
         (normal temp 37.7-38.6 C, dog pulse 80-120, cat pulse 180-200; red: dog HR >200 or <60,
         cat HR >250 or <150). */
      vitals: {
        dog: { t: [37.7, 38.6], tAmberHi: 40.0, tRedLo: 36.5, hr: [80, 120], hrRedHi: 200, hrRedLo: 60, rr: [10, 40], rrRedHi: 50 },
        cat: { t: [37.7, 38.6], tAmberHi: 40.0, tRedLo: 36.5, hr: [180, 200], hrRedHi: 250, hrRedLo: 150, rr: [20, 40], rrRedHi: 60 }
      },

      /* Normal values for triage - shown as a reference dropdown in the vitals step and
         as a static table in the reference section (both rendered from this array). */
      triageNormals: [
        { param: "Temperature",             dog: "37.7 to 38.6 C", cat: "37.7 to 38.6 C", note: "Above 40 C or below 36.5 C is flagged red" },
        { param: "Pulse / heart rate",      dog: "80 to 120 bpm",  cat: "180 to 200 bpm", note: "Red: dog over 200 or under 60; cat over 250 or under 150" },
        { param: "Respiratory rate",        dog: "10 to 40 brpm",  cat: "20 to 40 brpm",  note: "Count at rest; panting and stress raise it" },
        { param: "Gum (MM) colour",         dog: "Pink",           cat: "Pink",           note: "White, blue or brown gums are flagged red" },
        { param: "CRT",                     dog: "1 to 2 seconds", cat: "1 to 2 seconds", note: "Under 1 s or over 2 s is abnormal; over 3 s is red" },
        { param: "Mentation",               dog: "Alert",          cat: "Alert",          note: "Obtunded, stupor, coma or uncontrolled excitability is red" },
        { param: "Pulse quality (femoral)", dog: "Strong, regular", cat: "Strong, regular", note: "Weak or absent pulses are red; bounding is a caution" },
        { param: "Systolic blood pressure", dog: "Over 90 mmHg",   cat: "Over 90 mmHg",   note: "Under 90 mmHg is flagged red (shock until proven otherwise)" },
        { param: "Lactate",                 dog: "Under 2.5 mmol/L", cat: "Under 2.5 mmol/L", note: "2.5 and above suggests poor perfusion; 5 and above is red" }
      ],

      history: [
        { id: "h-vet",     t: "txt", text: "Regular vet, and when they last visited" },
        { id: "h-refer",   t: "txt", text: "Seen anywhere else for this problem, or referred? What was done?" },
        { id: "h-meds",    t: "txt", text: "Current medications and doses, including anything given today" },
        { id: "h-cond",    t: "txt", text: "Known conditions and past surgeries" },
        { id: "h-drugrx",  t: "txt", text: "Any previous drug reactions or sensitivities" },
        { id: "h-appetite",t: "sel", text: "Appetite", opts: [
            { v: "", label: "Select" }, { v: "normal", label: "Normal" }, { v: "reduced", label: "Reduced" },
            { v: "none", label: "Not eating" }, { v: "increased", label: "Increased" } ] },
        { id: "h-diet",    t: "txt", text: "Current diet, and any recent change or new food" },
        { id: "h-drink",   t: "sel", text: "Drinking", opts: [
            { v: "", label: "Select" }, { v: "normal", label: "Normal amount" }, { v: "more", label: "More than usual" },
            { v: "less", label: "Less than usual" }, { v: "none", label: "Not drinking" } ] },
        { id: "h-urine",   t: "txt", text: "Urination: frequency, colour and amount" },
        { id: "h-stool",   t: "txt", text: "Faeces: consistency, colour and amount" },
        { id: "h-vomit",   t: "txt", text: "Vomiting: how often and what colour" },
        { id: "h-scav",    t: "chk", text: "Tends to eat things it shouldn't" },
        { id: "h-access",  t: "chk", text: "Possible access to baits, poisons, medications, toxic plants or rubbish" },
        { id: "h-vacc",    t: "sel", text: "Vaccination status", opts: [
            { v: "", label: "Select" }, { v: "current", label: "Up to date" }, { v: "overdue", label: "Overdue" },
            { v: "never", label: "Never vaccinated" }, { v: "unknown", label: "Unknown" } ] },
        { id: "h-hw",      t: "txt", text: "Heartworm prevention: product and when last given" },
        { id: "h-flea",    t: "txt", text: "Flea and tick control: product and when last given" },
        { id: "h-worm",    t: "txt", text: "Worming: product and when last given" },
        { id: "h-lifestyle", t: "sel", text: "Lifestyle", opts: [
            { v: "", label: "Select" }, { v: "indoor", label: "Indoors only" }, { v: "both", label: "Indoors and outdoors" },
            { v: "outdoor", label: "Mostly outdoors" } ] },
        { id: "h-others",  t: "txt", text: "Other animals at home, and are they well?" },
        { id: "h-travel",  t: "chk", text: "Has travelled with the owner or came from overseas" },
        { id: "h-since",   t: "chk", text: "Owned since a puppy or kitten" }
      ],

      faqs: [
        { q: "What questions should a vet nurse ask at emergency triage?",
          a: "Start with a red-flag screen: breathing difficulty, gum colour, ability to stand, seizures, unproductive retching with a bloated belly, straining to urinate, toxin exposure, major trauma, birthing trouble and severe pain. Any red flag means the pet goes straight through. Then ask complaint-specific questions (when it started, how often, blood, toxin access), and finish with a focused history: medications, appetite, drinking, urination, faeces, vomiting, past illness and preventative care." },
        { q: "What counts as a genuine veterinary emergency?",
          a: "Breathing difficulty, pale or blue gums, collapse, repeated or prolonged seizures, uncontrolled bleeding, suspected bloat (GDV), a blocked bladder (especially male cats), toxin ingestion, snake bite, heatstroke, major trauma such as being hit by a car, birthing trouble, an eye out of its socket, and severe pain. These are seen immediately, ahead of the queue." },
        { q: "Why does the nurse take some pets straight through ahead of others?",
          a: "Triage orders patients by how quickly they could deteriorate, not by arrival time. A dog with bloat or a cat that cannot urinate can become unsalvageable in hours, while a limping but stable dog can safely wait. Being taken through first is about physiology, not favouritism." },
        { q: "Should I feed my pet while we wait at the emergency vet?",
          a: "No. Keep them off food and water until the vet advises, because many emergency patients need sedation, anaesthesia, imaging or surgery, and a full stomach adds risk and delay. Exception: only if the veterinary team specifically instructs otherwise, for example some diabetic patients." },
        { q: "What should I bring to an emergency vet visit after a poisoning?",
          a: "The packaging, label, bait tray or a photo of whatever was eaten, plus an estimate of how much is missing and when it happened. Do not induce vomiting at home unless a vet has told you to; with some toxins and in some patients it makes things worse." },
        { q: "How are triage categories decided in this tool?",
          a: "Deterministically. Any red flag or red-flagged answer suggests RED (immediate). Any amber answer suggests ORANGE (vet within 15 minutes). With no flags the tool suggests GREEN and the nurse confirms or upgrades on judgement. The nurse can always upgrade; downgrading below the suggestion requires a recorded reason." },
        { q: "Is my dog vomiting an emergency?",
          a: "Once or twice in a bright dog that keeps water down can usually be monitored or seen routinely. It becomes an emergency with any of: trying to vomit with nothing coming up plus a swelling belly (possible bloat or GDV), a lot of blood or vomit like coffee grounds, repeated vomiting that will not stop, a flat or weak dog, or an unvaccinated puppy, where parvovirus is a real risk." },
        { q: "What are the signs of bloat (GDV) in dogs?",
          a: "Unproductive retching (trying to vomit with nothing coming up), a swollen or tight belly, restlessness and pacing, drooling, and then weakness or collapse, most often in large deep-chested breeds after eating. GDV is immediately life-threatening: survival falls with every hour to surgery, so it is a straight-through RED at any clinic." },
        { q: "How quickly does a blocked cat need to be seen?",
          a: "Immediately. A cat straining to urinate with little or nothing coming out may have a urethral obstruction, which pushes blood potassium high enough to stop the heart, usually within 24 to 48 hours of blocking but sometimes faster. Male cats are affected most. This is never a wait-in-the-queue presentation." },
        { q: "What should I tell the vet nurse when I arrive at an emergency clinic?",
          a: "Lead with the main problem and when your pet was last completely normal. Then: any chance of poisons, baits or medications (bring the packaging), current medications, known conditions, and whether the problem is getting worse. Short, specific answers help the nurse triage accurately; everything else can follow once the pet has been assessed." },
        { q: "Why was my pet seen after animals that arrived later than us?",
          a: "Emergency clinics run on triage, not arrival order. Patients are seen in order of how quickly they could deteriorate: a breathing crisis or a blocked bladder goes ahead of a stable limp, whatever the queue order. If your pet gets worse while you wait, tell the team immediately and they will re-triage on the spot." },
        { q: "Can veterinary nurses perform triage assessments?",
          a: "Yes. Triage is a core nursing skill: a trained veterinary nurse screens for red flags, takes a focused history, records vitals where appropriate, and assigns a waiting category under the clinic's triage policy, escalating to the veterinarian anything urgent or unclear. Structured question sets like this one keep that process consistent between staff and shifts." }
      ]
    };


    /* ASK A VET (TM) Emergency Triage Questions - app logic. */
    "use strict";
      var D = AAV_DATA;
      var $ = function (id) { return root.querySelector("#" + id); };
      var LSKEY = "aav-triage-v1-" + (root.dataset.sectionId || "default");

      var state = {
        species: "",
        fields: {},        // pname, breed, age, sex, weight, owner, arrival, complaintFree, lastnormal, notes, nurse
        redflags: {},      // id -> true
        complaint: "",
        answers: {},       // question id -> true | select value | text
        vitals: {},        // temp, hr, rr, mm, crt, pain
        history: {},       // id -> value
        override: "",
        ovrReason: ""
      };
      if (root.dataset.defaultSpecies && root.dataset.defaultSpecies !== "none") state.species = root.dataset.defaultSpecies;

      function esc(s) {
        return String(s == null ? "" : s)
          .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
      }
      function norm(s) { return String(s || "").replace(/\s+/g, " ").trim(); }
      function catById(id) {
        for (var i = 0; i < D.categories.length; i++) if (D.categories[i].id === id) return D.categories[i];
        return null;
      }
      function complaintById(id) {
        for (var i = 0; i < D.complaints.length; i++) if (D.complaints[i].id === id) return D.complaints[i];
        return null;
      }
      function parseNum(s) {
        var t = String(s == null ? "" : s).trim();
        if (!t) return null;
        t = t.replace(",", ".");
        if (!/^\d+(\.\d+)?$/.test(t)) return NaN;
        return parseFloat(t);
      }

      /* ---------- build static-ish controls ---------- */

      function buildSpecies() {
        var host = $("aav-species");
        var html = "";
        D.species.forEach(function (s) {
          html += '<button type="button" data-sp="' + s.id + '" aria-pressed="false"><i class="em" aria-hidden="true">' + s.em + "</i>" + esc(s.label) + "</button>";
        });
        host.innerHTML = html;
        host.addEventListener("click", function (e) {
          var b = e.target.closest("button[data-sp]");
          if (!b) return;
          state.species = (state.species === b.getAttribute("data-sp")) ? "" : b.getAttribute("data-sp");
          syncSpecies();
          update();
        });
      }
      function syncSpecies() {
        var btns = $("aav-species").querySelectorAll("button[data-sp]");
        for (var i = 0; i < btns.length; i++) {
          btns[i].setAttribute("aria-pressed", btns[i].getAttribute("data-sp") === state.species ? "true" : "false");
        }
      }

      function buildRedflags() {
        var host = $("aav-redflags");
        var html = "";
        D.redflags.forEach(function (rf) {
          html += '<li class="qrow" data-row="' + rf.id + '"><input type="checkbox" id="' + rf.id + '"><label for="' + rf.id + '">' + esc(rf.text) + '</label><span class="qtag red">RED</span></li>';
        });
        host.innerHTML = html;
        host.addEventListener("change", function (e) {
          var cb = e.target;
          if (cb && cb.type === "checkbox") {
            state.redflags[cb.id] = cb.checked;
            var row = host.querySelector('[data-row="' + cb.id + '"]');
            if (row) row.classList.toggle("red-on", cb.checked);
            update();
          }
        });
      }
      function syncRedflags() {
        D.redflags.forEach(function (rf) {
          var cb = $(rf.id);
          if (!cb) return;
          cb.checked = !!state.redflags[rf.id];
          var row = $("aav-redflags").querySelector('[data-row="' + rf.id + '"]');
          if (row) row.classList.toggle("red-on", cb.checked);
        });
      }

      function buildComplaints() {
        var host = $("aav-complaints");
        var html = "";
        D.complaints.forEach(function (c) {
          html += '<button type="button" data-c="' + c.id + '" aria-pressed="false"><i class="em" aria-hidden="true">' + c.em + "</i>" + esc(c.label) + "</button>";
        });
        host.innerHTML = html;
        host.addEventListener("click", function (e) {
          var b = e.target.closest("button[data-c]");
          if (!b) return;
          var id = b.getAttribute("data-c");
          state.complaint = (state.complaint === id) ? "" : id;
          syncComplaints();
          renderCQuestions();
          update();
        });
      }
      function syncComplaints() {
        var btns = $("aav-complaints").querySelectorAll("button[data-c]");
        for (var i = 0; i < btns.length; i++) {
          btns[i].setAttribute("aria-pressed", btns[i].getAttribute("data-c") === state.complaint ? "true" : "false");
        }
      }

      function questionRowHtml(q) {
        if (q.t === "note") {
          var lv = q.level === "red" ? "red" : (q.level === "amber" ? "amber" : "info");
          return '<div class="warn ' + lv + '" style="margin-top:8px">' + esc(q.text) + "</div>";
        }
        if (q.t === "chk") {
          var tag = q.level === "red" ? '<span class="qtag red">RED</span>' : (q.level === "amber" ? '<span class="qtag amber">AMBER</span>' : "");
          return '<li class="qrow" data-row="' + q.id + '"><input type="checkbox" id="' + q.id + '" data-q="' + q.id + '"><label for="' + q.id + '">' + esc(q.text) + "</label>" + tag + "</li>";
        }
        if (q.t === "sel") {
          var o = "";
          q.opts.forEach(function (op) {
            o += '<option value="' + esc(op.v) + '">' + esc(op.label) + "</option>";
          });
          return '<div class="qsel" data-row="' + q.id + '"><span id="lbl-' + q.id + '">' + esc(q.text) + '</span><select id="' + q.id + '" data-q="' + q.id + '" aria-labelledby="lbl-' + q.id + '">' + o + "</select></div>";
        }
        /* txt */
        return '<div style="margin-top:8px"><label class="lbl" for="' + q.id + '" style="text-transform:none;letter-spacing:0;font-size:14px;font-weight:600">' + esc(q.text) + '</label><input type="text" id="' + q.id + '" data-q="' + q.id + '" autocomplete="off"></div>';
      }

      function renderCQuestions() {
        var host = $("aav-cquestions");
        var c = complaintById(state.complaint);
        if (!c) {
          host.innerHTML = '<p class="hint" style="margin-top:8px">Select a complaint above to load its question set. If nothing fits, use "Something else".</p>';
          return;
        }
        var html = '<ul class="qlist" style="margin-top:8px">';
        var inList = true;
        c.qs.forEach(function (q) {
          if (q.t === "chk") {
            if (!inList) { html += '<ul class="qlist" style="margin-top:8px">'; inList = true; }
            html += questionRowHtml(q);
          } else {
            if (inList) { html += "</ul>"; inList = false; }
            html += questionRowHtml(q);
          }
        });
        if (inList) html += "</ul>";
        host.innerHTML = html;
        /* restore stored answers */
        c.qs.forEach(function (q) {
          if (q.t === "note") return;
          var el = $(q.id);
          if (!el) return;
          var v = state.answers[q.id];
          if (q.t === "chk") { el.checked = v === true; markRow(q, el); }
          else if (v != null) { el.value = v; if (q.t === "sel") markRow(q, el); }
        });
        host.oninput = host.onchange = function (e) {
          var el = e.target;
          var qid = el.getAttribute && el.getAttribute("data-q");
          if (!qid) return;
          var q = findQuestion(qid);
          if (!q) return;
          if (q.t === "chk") state.answers[qid] = el.checked;
          else state.answers[qid] = el.value;
          markRow(q, el);
          update();
        };
      }

      function findQuestion(qid) {
        var c = complaintById(state.complaint);
        if (!c) return null;
        for (var i = 0; i < c.qs.length; i++) if (c.qs[i].id === qid) return c.qs[i];
        return null;
      }

      function markRow(q, el) {
        var row = root.querySelector('#aav-cquestions [data-row="' + q.id + '"]');
        if (!row) return;
        var lvl = null;
        if (q.t === "chk" && el.checked) lvl = q.level || null;
        if (q.t === "sel") {
          var op = optByV(q, el.value);
          lvl = op && op.level ? op.level : null;
        }
        row.classList.toggle("red-on", lvl === "red");
        row.classList.toggle("amber-on", lvl === "amber");
      }
      function optByV(q, v) {
        if (!q.opts) return null;
        for (var i = 0; i < q.opts.length; i++) if (q.opts[i].v === v) return q.opts[i];
        return null;
      }

      function buildHistory() {
        var host = $("aav-history");
        var html = "";
        var chks = [];
        D.history.forEach(function (h) {
          if (h.t === "chk") { chks.push(h); return; }
          if (h.t === "sel") {
            var o = "";
            h.opts.forEach(function (op) { o += '<option value="' + esc(op.v) + '">' + esc(op.label) + "</option>"; });
            html += '<div class="qsel" data-row="' + h.id + '" style="margin-top:8px"><span id="lbl-' + h.id + '">' + esc(h.text) + '</span><select id="' + h.id + '" data-h="' + h.id + '" aria-labelledby="lbl-' + h.id + '">' + o + "</select></div>";
          } else {
            html += '<div style="margin-top:8px"><label class="lbl" for="' + h.id + '" style="text-transform:none;letter-spacing:0;font-size:14px;font-weight:600">' + esc(h.text) + '</label><input type="text" id="' + h.id + '" data-h="' + h.id + '" autocomplete="off"></div>';
          }
        });
        html += '<ul class="qlist" style="margin-top:10px">';
        chks.forEach(function (h) {
          html += '<li class="qrow" data-row="' + h.id + '"><input type="checkbox" id="' + h.id + '" data-h="' + h.id + '"><label for="' + h.id + '">' + esc(h.text) + "</label></li>";
        });
        html += "</ul>";
        host.innerHTML = html;
        host.oninput = host.onchange = function (e) {
          var el = e.target;
          var hid = el.getAttribute && el.getAttribute("data-h");
          if (!hid) return;
          state.history[hid] = (el.type === "checkbox") ? el.checked : el.value;
          update();
        };
      }
      function syncHistory() {
        D.history.forEach(function (h) {
          var el = $(h.id);
          if (!el) return;
          var v = state.history[h.id];
          if (h.t === "chk") el.checked = v === true;
          else el.value = v == null ? "" : v;
        });
      }

      /* ---------- flag engine ---------- */

      function vitalFlags() {
        var out = [];
        var sp = state.species;
        var ranges = (sp === "dog" || sp === "cat") ? D.vitals[sp] : null;
        var t = parseNum(state.vitals.temp);
        var hr = parseNum(state.vitals.hr);
        var rr = parseNum(state.vitals.rr);
        function bad(v) { return typeof v === "number" && isNaN(v); }
        if (bad(t)) out.push({ level: "info", text: "Temperature entry not recognised as a number" });
        if (bad(hr)) out.push({ level: "info", text: "Heart rate entry not recognised as a number" });
        if (bad(rr)) out.push({ level: "info", text: "Respiratory rate entry not recognised as a number" });
        if (ranges) {
          if (t != null && !isNaN(t)) {
            if (t > 43 || t < 30) out.push({ level: "info", text: "Temperature " + t + " C is outside plausible range: recheck the reading" });
            else if (t > ranges.tAmberHi) out.push({ level: "red", text: "Temperature " + t + " C: marked hyperthermia" });
            else if (t > ranges.t[1]) out.push({ level: "amber", text: "Temperature " + t + " C: above normal (" + ranges.t[0] + " to " + ranges.t[1] + ")" });
            else if (t < ranges.tRedLo) out.push({ level: "red", text: "Temperature " + t + " C: marked hypothermia" });
            else if (t < ranges.t[0]) out.push({ level: "amber", text: "Temperature " + t + " C: below normal (" + ranges.t[0] + " to " + ranges.t[1] + ")" });
          }
          if (hr != null && !isNaN(hr)) {
            if (hr > 400 || hr < 10) out.push({ level: "info", text: "Heart rate " + hr + " is outside plausible range: recheck the reading" });
            else if (hr > ranges.hrRedHi) out.push({ level: "red", text: "Heart rate " + hr + ": marked tachycardia for a " + sp });
            else if (hr > ranges.hr[1]) out.push({ level: "amber", text: "Heart rate " + hr + ": above the resting range (" + ranges.hr[0] + " to " + ranges.hr[1] + ")" });
            else if (hr < ranges.hrRedLo) out.push({ level: "red", text: "Heart rate " + hr + ": marked bradycardia for a " + sp });
            else if (hr < ranges.hr[0]) out.push({ level: "amber", text: "Heart rate " + hr + ": below the resting range (" + ranges.hr[0] + " to " + ranges.hr[1] + ")" });
          }
          if (rr != null && !isNaN(rr)) {
            if (rr > 250 || rr < 2) out.push({ level: "info", text: "Respiratory rate " + rr + " is outside plausible range: recheck the reading" });
            else if (rr > ranges.rrRedHi) out.push({ level: "red", text: "Respiratory rate " + rr + ": severe tachypnoea" });
            else if (rr > ranges.rr[1]) out.push({ level: "amber", text: "Respiratory rate " + rr + ": above the resting range (" + ranges.rr[0] + " to " + ranges.rr[1] + ")" });
            else if (rr < ranges.rr[0]) out.push({ level: "amber", text: "Respiratory rate " + rr + ": below the resting range (" + ranges.rr[0] + " to " + ranges.rr[1] + ")" });
          }
        } else if ((t != null && !isNaN(t)) || (hr != null && !isNaN(hr)) || (rr != null && !isNaN(rr))) {
          out.push({ level: "info", text: "Automatic vital ranges apply to dogs and cats only: interpret these values on species knowledge" });
        }
        var mm = state.vitals.mm;
        if (mm === "white" || mm === "blue") out.push({ level: "red", text: "Gum colour " + (mm === "white" ? "white" : "blue or grey") + ": treat as shock or hypoxia" });
        else if (mm === "brown") out.push({ level: "red", text: "Brown or muddy gums: possible methaemoglobinaemia (paracetamol, onion, permethrin)" });
        else if (mm === "pale") out.push({ level: "amber", text: "Gums pale pink: possible early shock, blood loss or anaemia" });
        else if (mm === "red") out.push({ level: "amber", text: "Gums brick red or injected: consider heat, sepsis or toxins" });
        else if (mm === "yellow") out.push({ level: "amber", text: "Gums yellow: possible jaundice" });
        else if (mm === "petechiae") out.push({ level: "amber", text: "Pinpoint bruising on the gums: possible platelet or clotting problem, handle gently" });
        var crt = state.vitals.crt;
        if (crt === "gt3") out.push({ level: "red", text: "CRT over 3 seconds: poor perfusion, decompensating shock" });
        else if (crt === "2to3") out.push({ level: "amber", text: "CRT 2 to 3 seconds: reduced perfusion or vasoconstriction" });
        else if (crt === "lt1") out.push({ level: "amber", text: "CRT under 1 second: hyperdynamic state (early shock, sepsis, heat)" });
        var ment = state.vitals.ment;
        if (ment === "obtunded" || ment === "stupor" || ment === "coma") {
          var mword = ment === "obtunded" ? "Obtunded" : (ment === "stupor" ? "Stuporous" : "Comatose");
          out.push({ level: "red", text: mword + " mentation: consider shock, hypoxia, low blood glucose, toxins or brain disease" });
        } else if (ment === "hyper") out.push({ level: "red", text: "Uncontrolled excitability: consider toxins (snail bait, chocolate, stimulants)" });
        else if (ment === "quiet") out.push({ level: "amber", text: "Quieter than expected: recheck mentation on every reassessment" });
        var pulse = state.vitals.pulse;
        if (pulse === "absent") out.push({ level: "red", text: "No palpable femoral pulse: treat as shock or arrest risk, take through now" });
        else if (pulse === "weak") out.push({ level: "red", text: "Weak or thready femoral pulses: poor perfusion" });
        else if (pulse === "bounding") out.push({ level: "amber", text: "Bounding pulses: hyperdynamic state (early shock, sepsis, heat)" });
        else if (pulse === "irregular") out.push({ level: "amber", text: "Irregular pulses or missing beats: possible arrhythmia, needs an ECG" });
        var sbp = parseNum(state.vitals.sbp);
        if (typeof sbp === "number" && isNaN(sbp)) out.push({ level: "info", text: "Blood pressure entry not recognised as a number" });
        else if (sbp != null) {
          if (sbp < 30 || sbp > 300) out.push({ level: "info", text: "Systolic BP " + sbp + " mmHg is outside plausible range: recheck the reading" });
          else if (sbp < 90) out.push({ level: "red", text: "Systolic BP " + sbp + " mmHg: hypotension, shock until proven otherwise" });
        }
        var lact = parseNum(state.vitals.lact);
        if (typeof lact === "number" && isNaN(lact)) out.push({ level: "info", text: "Lactate entry not recognised as a number" });
        else if (lact != null) {
          if (lact > 25) out.push({ level: "info", text: "Lactate " + lact + " mmol/L is outside plausible range: recheck the reading" });
          else if (lact >= 5) out.push({ level: "red", text: "Lactate " + lact + " mmol/L: severe hyperlactataemia, poor perfusion" });
          else if (lact >= 2.5) out.push({ level: "amber", text: "Lactate " + lact + " mmol/L: above normal (under 2.5), suggests reduced perfusion" });
        }
        var pain = state.vitals.pain;
        if (pain === "4") out.push({ level: "red", text: "Pain score 4 of 4: severe pain" });
        else if (pain === "3") out.push({ level: "amber", text: "Pain score 3 of 4: marked pain" });
        return out;
      }

      function collectFlags() {
        var flags = [];   // {level, text}
        var iso = false;
        D.redflags.forEach(function (rf) {
          if (state.redflags[rf.id]) {
            flags.push({ level: "red", text: rf.text });
            if (rf.iso) iso = true;
          }
        });
        var c = complaintById(state.complaint);
        if (c) {
          c.qs.forEach(function (q) {
            if (q.t === "chk" && state.answers[q.id] === true) {
              if (q.level) flags.push({ level: q.level, text: q.flag || q.text });
              else if (q.iso) flags.push({ level: "info", text: q.flag || q.text });
              if (q.iso) iso = true;
            }
            if (q.t === "sel") {
              var op = optByV(q, state.answers[q.id]);
              if (op && op.level) flags.push({ level: op.level, text: op.flag || op.label });
              else if (op && op.iso) flags.push({ level: "info", text: op.flag || op.label });
              if (op && op.iso) iso = true;
            }
          });
        }
        vitalFlags().forEach(function (f) { flags.push(f); });
        var order = { red: 0, amber: 1, info: 2 };
        flags.sort(function (a, b) {
          var ra = order[a.level], rb = order[b.level];
          if (ra === undefined) ra = 9;
          if (rb === undefined) rb = 9;
          return ra - rb;
        });
        return { flags: flags, iso: iso };
      }

      function anyInteraction() {
        if (state.complaint) return true;
        for (var k in state.redflags) if (state.redflags[k]) return true;
        var f = vitalFlags();
        if (f.length) return true;
        return false;
      }

      function suggestedCat(flags) {
        if (!anyInteraction()) return null;
        for (var i = 0; i < flags.length; i++) if (flags[i].level === "red") return "red";
        for (var j = 0; j < flags.length; j++) if (flags[j].level === "amber") return "orange";
        return "green";
      }

      /* ---------- rendering result ---------- */

      var srTimer = null;
      function update() {
        var res = collectFlags();
        var sug = suggestedCat(res.flags);
        var eff = state.override || sug;
        var sugCat = sug ? catById(sug) : null;
        var effCat = eff ? catById(eff) : null;
        var downgraded = sug && state.override && catById(state.override).rank > catById(sug).rank;

        var band = $("aav-catband");
        band.className = "catband " + (effCat ? "c-" + effCat.id : "c-none");
        $("aav-catname").textContent = effCat ? effCat.name : "NOT SET";
        $("aav-catsub").textContent = effCat ? (effCat.title + " - " + effCat.sub) : "Work through the red-flag screen and complaint questions";

        $("aav-isoband").hidden = !res.iso;

        var counts = { red: 0, amber: 0, info: 0 };
        var fhtml = "";
        res.flags.forEach(function (f) {
          counts[f.level] = (counts[f.level] || 0) + 1;
          var word = f.level === "red" ? "Critical." : (f.level === "amber" ? "Caution." : "Note.");
          fhtml += '<li class="f-' + f.level + '"><b>' + word + "</b> " + esc(f.text) + "</li>";
        });
        if (!res.flags.length && sug) {
          fhtml = '<li class="f-info"><b>Note.</b> No automatic flags from the answers so far. Confirm GREEN or upgrade on your judgement.</li>';
        }
        $("aav-rflags").innerHTML = fhtml;

        /* override */
        var wrap = $("aav-ovr-reason-wrap");
        wrap.hidden = !downgraded;
        var ovrWarn = "";
        if (downgraded) {
          ovrWarn = state.ovrReason && norm(state.ovrReason) ? "" : "Downgrade below the suggested category needs a recorded reason.";
        }

        /* care list */
        var carebox = $("aav-carebox");
        if (effCat) {
          carebox.hidden = false;
          var chtml = "";
          effCat.care.forEach(function (cline) { chtml += "<li>" + esc(cline) + "</li>"; });
          $("aav-carelist").innerHTML = chtml;
        } else {
          carebox.hidden = true;
        }

        /* meta */
        var meta = [];
        if (sugCat) meta.push("Suggested: " + sugCat.name);
        if (state.override && effCat) meta.push("Nurse set: " + effCat.name);
        if (ovrWarn) meta.push(ovrWarn);
        if (state.fields.arrival) meta.push("Arrived " + state.fields.arrival);
        meta.push("Saved automatically on this device");
        $("aav-rmeta").textContent = meta.join(" · ");

        /* pinbar */
        var pincat = $("aav-pincat");
        pincat.className = "pcat " + (effCat ? "c-" + effCat.id : "c-none");
        pincat.textContent = effCat ? effCat.name : "NOT SET";
        var pmsg = "Work through the screen above";
        if (effCat) {
          if (counts.red) pmsg = counts.red + " red flag" + (counts.red > 1 ? "s" : "") + " · " + effCat.when;
          else if (counts.amber) pmsg = counts.amber + " caution" + (counts.amber > 1 ? "s" : "") + " · " + effCat.when;
          else pmsg = effCat.sub;
        }
        $("aav-pinmsg").textContent = pmsg;

        /* debounced screen reader summary */
        if (srTimer) clearTimeout(srTimer);
        srTimer = setTimeout(function () {
          var s = effCat ? ("Triage category " + effCat.name + ", " + effCat.title + ". ") : "Triage category not set. ";
          s += counts.red + " critical, " + counts.amber + " caution flags.";
          if (res.iso) s += " Isolation advised.";
          $("aav-srlive").textContent = s;
        }, 900);

        save();
      }

      /* ---------- persistence ---------- */

      var saveTimer = null;
      function save() {
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(function () {
          /* never let an empty state clobber a saved patient record */
          if (!hasContent(state)) return;
          try {
            localStorage.setItem(LSKEY, JSON.stringify({ v: 1, ts: new Date().toISOString(), state: state }));
          } catch (e) { /* private mode etc */ }
        }, 400);
      }
      function loadSaved() {
        try {
          var raw = localStorage.getItem(LSKEY);
          if (!raw) return null;
          var obj = JSON.parse(raw);
          if (!obj || obj.v !== 1 || !obj.state) return null;
          return obj;
        } catch (e) { return null; }
      }
      function hasContent(st) {
        if (!st) return false;
        if (st.complaint || st.species || st.override) return true;
        var k;
        for (k in st.redflags) if (st.redflags[k]) return true;
        for (k in st.fields) if (st.fields[k]) return true;
        for (k in st.answers) if (st.answers[k]) return true;
        for (k in st.vitals) if (st.vitals[k]) return true;
        for (k in st.history) if (st.history[k]) return true;
        return false;
      }
      function applySaved(st) {
        state = st;
        state.fields = state.fields || {};
        state.redflags = state.redflags || {};
        state.answers = state.answers || {};
        state.vitals = state.vitals || {};
        state.history = state.history || {};
        /* validate stored ids against data */
        if (state.complaint && !complaintById(state.complaint)) state.complaint = "";
        if (state.override && !catById(state.override)) state.override = "";
        syncSpecies(); syncRedflags(); syncComplaints(); renderCQuestions(); syncHistory();
        FIELD_IDS.forEach(function (f) { var el = $(f.el); if (el) el.value = state.fields[f.key] || ""; });
        if (state.vitals.crt === "lt2") state.vitals.crt = "1to2"; /* migrate pre-protocol saves */
        VKEYS.forEach(function (v) {
          var el = $("aav-" + v); if (el) el.value = state.vitals[v] || "";
        });
        $("aav-ovr").value = state.override || "";
        $("aav-ovr-reason").value = state.ovrReason || "";
        renderVitalWarns();
        update();
      }

      /* ---------- field wiring ---------- */

      var VKEYS = ["temp", "hr", "rr", "mm", "crt", "pain", "ment", "pulse", "sbp", "lact"];
      var FIELD_IDS = [
        { el: "aav-pname", key: "pname" }, { el: "aav-breed", key: "breed" }, { el: "aav-age", key: "age" },
        { el: "aav-sex", key: "sex" }, { el: "aav-weight", key: "weight" }, { el: "aav-owner", key: "owner" },
        { el: "aav-arrival", key: "arrival" }, { el: "aav-complaint-free", key: "complaintFree" },
        { el: "aav-lastnormal", key: "lastnormal" }, { el: "aav-notes", key: "notes" }, { el: "aav-nurse", key: "nurse" }
      ];

      function renderVitalWarns() {
        var host = $("aav-vitalflags");
        var f = vitalFlags();
        var html = "";
        f.forEach(function (w) {
          var lv = w.level === "red" ? "red" : (w.level === "amber" ? "amber" : "info");
          var word = w.level === "red" ? "Critical." : (w.level === "amber" ? "Caution." : "Note.");
          html += '<div class="warn ' + lv + '"><b>' + word + "</b> " + esc(w.text) + "</div>";
        });
        host.innerHTML = html;
      }

      /* ---------- text record ---------- */

      function line(label, val) { return val && norm(val) ? label + ": " + norm(val) + "\n" : ""; }
      function crtLabel(v) {
        return v === "lt1" ? "under 1 s" : v === "1to2" || v === "lt2" ? "1 to 2 s" : v === "2to3" ? "2 to 3 s" : v === "gt3" ? "over 3 s" : v;
      }
      function mentLabel(v) {
        return v === "alert" ? "alert" : v === "quiet" ? "quiet but responsive" : v === "obtunded" ? "obtunded" :
          v === "stupor" ? "stupor" : v === "coma" ? "coma" : v === "hyper" ? "uncontrolled excitability" : v;
      }

      function buildText() {
        var res = collectFlags();
        var sug = suggestedCat(res.flags);
        var eff = state.override || sug;
        var effCat = eff ? catById(eff) : null;
        var sugCat = sug ? catById(sug) : null;
        var downgraded = sug && state.override && catById(state.override).rank > catById(sug).rank;
        var sp = null;
        D.species.forEach(function (s) { if (s.id === state.species) sp = s; });
        var now = new Date();
        var t = "ASK A VET (TM) EMERGENCY TRIAGE SHEET\n";
        t += "Generated: " + now.toLocaleString() + "\n";
        t += "==============================\n\n";
        t += "TRIAGE CATEGORY: " + (effCat ? effCat.name + " (" + effCat.title + " - " + effCat.when + ")" : "NOT SET") + "\n";
        if (sugCat && state.override && state.override !== sug) {
          t += "Suggested by tool: " + sugCat.name + " / set by nurse: " + effCat.name + "\n";
          if (downgraded) t += "Downgrade reason: " + (norm(state.ovrReason) || "NOT RECORDED") + "\n";
        }
        if (res.iso) t += "INFECTION RISK: isolate, do not hold in the shared waiting room\n";
        t += "\nPATIENT\n";
        t += line("Name", state.fields.pname);
        t += line("Species", sp ? sp.label : "");
        t += line("Breed", state.fields.breed);
        t += line("Age", state.fields.age);
        t += line("Sex", state.fields.sex);
        t += line("Weight (kg)", state.fields.weight);
        t += line("Owner", state.fields.owner);
        t += line("Arrived", state.fields.arrival);
        t += line("Reason (owner's words)", state.fields.complaintFree);
        t += line("Last completely normal", state.fields.lastnormal);
        if (res.flags.length) {
          t += "\nFLAGS\n";
          res.flags.forEach(function (f) {
            var word = f.level === "red" ? "[RED] " : (f.level === "amber" ? "[AMBER] " : "[note] ");
            t += word + f.text + "\n";
          });
        }
        var c = complaintById(state.complaint);
        if (c) {
          t += "\nCOMPLAINT: " + c.label + "\n";
          c.qs.forEach(function (q) {
            if (q.t === "note") return;
            var v = state.answers[q.id];
            if (q.t === "chk" && v === true) t += "- " + q.text + ": YES\n";
            if (q.t === "sel" && v) {
              var op = optByV(q, v);
              if (op && op.v) t += "- " + q.text + " " + op.label + "\n";
            }
            if (q.t === "txt" && v && norm(v)) t += "- " + q.text + " " + norm(v) + "\n";
          });
        }
        var vit = [];
        if (norm(state.vitals.temp)) vit.push("Temp " + norm(state.vitals.temp) + " C");
        if (norm(state.vitals.hr)) vit.push("HR " + norm(state.vitals.hr));
        if (norm(state.vitals.rr)) vit.push("RR " + norm(state.vitals.rr));
        if (state.vitals.mm) vit.push("Gums " + state.vitals.mm);
        if (state.vitals.crt) vit.push("CRT " + crtLabel(state.vitals.crt));
        if (state.vitals.ment) vit.push("Mentation " + mentLabel(state.vitals.ment));
        if (state.vitals.pulse) vit.push("Pulse " + state.vitals.pulse);
        if (norm(state.vitals.sbp)) vit.push("SBP " + norm(state.vitals.sbp) + " mmHg");
        if (norm(state.vitals.lact)) vit.push("Lactate " + norm(state.vitals.lact) + " mmol/L");
        if (state.vitals.pain) vit.push("Pain " + state.vitals.pain + "/4");
        if (vit.length) t += "\nVITALS\n" + vit.join(", ") + "\n";
        var hx = "";
        D.history.forEach(function (h) {
          var v = state.history[h.id];
          if (h.t === "chk" && v === true) hx += "- " + h.text + ": YES\n";
          if (h.t === "sel" && v) {
            var op = optByV(h, v);
            if (op && op.v) hx += "- " + h.text + ": " + op.label + "\n";
          }
          if (h.t === "txt" && v && norm(v)) hx += "- " + h.text + ": " + norm(v) + "\n";
        });
        if (hx) t += "\nHISTORY\n" + hx;
        if (norm(state.fields.notes)) t += "\nNURSE NOTES\n" + norm(state.fields.notes) + "\n";
        if (effCat) {
          t += "\nWHILE WAITING\n";
          effCat.care.forEach(function (cl) { t += "- " + cl + "\n"; });
        }
        t += "\nTriaged by: " + (norm(state.fields.nurse) || "____________") + "\n";
        t += "\n------------------------------\n";
        t += "ASK A VET (TM) - askavet.com - Talk. Track. Trust.\n";
        t += "Triage record for veterinary staff. Not a diagnosis.\n";
        return t;
      }

      /* ---------- print sheet ---------- */

      function buildPrint() {
        var res = collectFlags();
        var sug = suggestedCat(res.flags);
        var eff = state.override || sug;
        var effCat = eff ? catById(eff) : null;
        var sugCat = sug ? catById(sug) : null;
        var downgraded = sug && state.override && catById(state.override).rank > catById(sug).rank;
        var sp = null;
        D.species.forEach(function (s) { if (s.id === state.species) sp = s; });
        var now = new Date();
        var h = '<div class="phead"><div class="avlogo" aria-hidden="true">AV</div><div><div class="ptitle">Emergency triage sheet</div><div class="psub">ASK A VET&trade; &middot; ' + esc(now.toLocaleString()) + "</div></div></div>";
        h += '<div class="pcatband ' + (effCat ? "c-" + effCat.id : "c-none") + '"><span>' + (effCat ? effCat.name + " &middot; " + esc(effCat.title) : "CATEGORY NOT SET") + '</span><span class="pwhen">' + (effCat ? esc(effCat.when) : "") + "</span></div>";
        if (sugCat && state.override && state.override !== sug) {
          h += '<div class="psub">Tool suggested ' + sugCat.name + "; nurse set " + effCat.name + "." + (downgraded ? " Reason: " + (esc(norm(state.ovrReason)) || "<b>not recorded</b>") : "") + "</div>";
        }
        if (res.iso) h += '<div class="piso">INFECTION RISK: isolate. Do not hold in the shared waiting room.</div>';
        h += "<h3>Patient</h3><table><tbody>";
        function prow(k, v) { if (v && norm(v)) h += "<tr><th>" + esc(k) + "</th><td>" + esc(norm(v)) + "</td></tr>"; }
        prow("Name", state.fields.pname);
        prow("Species", sp ? sp.label : "");
        prow("Breed", state.fields.breed);
        prow("Age", state.fields.age);
        prow("Sex", state.fields.sex);
        prow("Weight (kg)", state.fields.weight);
        prow("Owner", state.fields.owner);
        prow("Arrived", state.fields.arrival);
        prow("Reason (owner's words)", state.fields.complaintFree);
        prow("Last completely normal", state.fields.lastnormal);
        h += "</tbody></table>";
        if (res.flags.length) {
          h += "<h3>Flags</h3><ul>";
          res.flags.forEach(function (f) {
            var cls = f.level === "red" ? "pf-red" : (f.level === "amber" ? "pf-amber" : "");
            var word = f.level === "red" ? "RED: " : (f.level === "amber" ? "AMBER: " : "Note: ");
            h += '<li class="' + cls + '">' + word + esc(f.text) + "</li>";
          });
          h += "</ul>";
        }
        var c = complaintById(state.complaint);
        if (c) {
          var rows = "";
          c.qs.forEach(function (q) {
            if (q.t === "note") return;
            var v = state.answers[q.id];
            if (q.t === "chk" && v === true) rows += "<tr><th>" + esc(q.text) + "</th><td>Yes</td></tr>";
            if (q.t === "sel" && v) { var op = optByV(q, v); if (op && op.v) rows += "<tr><th>" + esc(q.text) + "</th><td>" + esc(op.label) + "</td></tr>"; }
            if (q.t === "txt" && v && norm(v)) rows += "<tr><th>" + esc(q.text) + "</th><td>" + esc(norm(v)) + "</td></tr>";
          });
          if (rows) h += "<h3>Complaint: " + esc(c.label) + "</h3><table><tbody>" + rows + "</tbody></table>";
          else h += "<h3>Complaint: " + esc(c.label) + "</h3>";
        }
        var vit = "";
        if (norm(state.vitals.temp)) vit += "<tr><th>Temperature</th><td>" + esc(norm(state.vitals.temp)) + " C</td></tr>";
        if (norm(state.vitals.hr)) vit += "<tr><th>Heart rate</th><td>" + esc(norm(state.vitals.hr)) + " bpm</td></tr>";
        if (norm(state.vitals.rr)) vit += "<tr><th>Respiratory rate</th><td>" + esc(norm(state.vitals.rr)) + " brpm</td></tr>";
        if (state.vitals.mm) vit += "<tr><th>Gum colour</th><td>" + esc(state.vitals.mm) + "</td></tr>";
        if (state.vitals.crt) vit += "<tr><th>CRT</th><td>" + esc(crtLabel(state.vitals.crt)) + "</td></tr>";
        if (state.vitals.ment) vit += "<tr><th>Mentation</th><td>" + esc(mentLabel(state.vitals.ment)) + "</td></tr>";
        if (state.vitals.pulse) vit += "<tr><th>Femoral pulse</th><td>" + esc(state.vitals.pulse) + "</td></tr>";
        if (norm(state.vitals.sbp)) vit += "<tr><th>Systolic BP</th><td>" + esc(norm(state.vitals.sbp)) + " mmHg</td></tr>";
        if (norm(state.vitals.lact)) vit += "<tr><th>Lactate</th><td>" + esc(norm(state.vitals.lact)) + " mmol/L</td></tr>";
        if (state.vitals.pain) vit += "<tr><th>Pain score</th><td>" + esc(state.vitals.pain) + " / 4</td></tr>";
        if (vit) h += "<h3>Vitals at triage</h3><table><tbody>" + vit + "</tbody></table>";
        var hx = "";
        D.history.forEach(function (hf) {
          var v = state.history[hf.id];
          if (hf.t === "chk" && v === true) hx += "<tr><th>" + esc(hf.text) + "</th><td>Yes</td></tr>";
          if (hf.t === "sel" && v) { var op = optByV(hf, v); if (op && op.v) hx += "<tr><th>" + esc(hf.text) + "</th><td>" + esc(op.label) + "</td></tr>"; }
          if (hf.t === "txt" && v && norm(v)) hx += "<tr><th>" + esc(hf.text) + "</th><td>" + esc(norm(v)) + "</td></tr>";
        });
        if (hx) h += "<h3>History</h3><table><tbody>" + hx + "</tbody></table>";
        if (norm(state.fields.notes)) h += "<h3>Nurse notes</h3><p>" + esc(norm(state.fields.notes)) + "</p>";
        if (effCat) {
          h += "<h3>While waiting</h3><ul>";
          effCat.care.forEach(function (cl) { h += "<li>" + esc(cl) + "</li>"; });
          h += "</ul>";
        }
        h += '<div class="psig"><div>Triage nurse: ' + esc(norm(state.fields.nurse) || "") + "</div><div>Veterinarian</div></div>";
        h += '<div class="pfoot"><span>ASK A VET&trade; &middot; askavet.com &middot; Talk. Track. Trust.</span><span>Triage record for veterinary staff. Not a diagnosis.</span></div>';
        $("aav-printsheet").innerHTML = h;
      }

      /* ---------- downloads ---------- */

      function blobSave(name, text) {
        try {
          var blob = new Blob([text], { type: "text/plain;charset=utf-8" });
          var url = URL.createObjectURL(blob);
          var a = document.createElement("a");
          a.href = url; a.download = name;
          document.body.appendChild(a); a.click();
          setTimeout(function () { document.body.removeChild(a); URL.revokeObjectURL(url); }, 400);
        } catch (e) {}
      }
      function saveTxt(name, text) {
        var w = window;
        if (typeof w.claude !== "undefined" && w.claude && typeof w.claude.use === "function") {
          w.claude.use("downloads").then(function (dl) {
            if (dl) { dl.save({ filename: name, data: text }).catch(function () {}); }
            else { blobSave(name, text); }
          }).catch(function () { blobSave(name, text); });
        } else {
          blobSave(name, text);
        }
      }

      /* ---------- integrity check ---------- */

      function integrity() {
        var bad = false;
        try {
          var rfRows = root.querySelectorAll("#ref-redflags-table tbody tr");
          if (rfRows.length !== D.redflags.length) bad = true;
          else for (var i = 0; i < rfRows.length; i++) {
            var cell = rfRows[i].querySelector("[data-rft]");
            if (!cell || norm(cell.textContent) !== norm(D.redflags[i].text)) { bad = true; break; }
          }
          var catRows = root.querySelectorAll("#ref-categories-table tbody tr");
          if (!bad && catRows.length !== D.categories.length) bad = true;
          if (!bad) for (var j = 0; j < catRows.length; j++) {
            var nm = catRows[j].querySelector("[data-catn]");
            var wh = catRows[j].querySelector("[data-catw]");
            if (!nm || !wh || norm(nm.textContent) !== D.categories[j].name || norm(wh.textContent) !== norm(D.categories[j].when)) { bad = true; break; }
          }
          if (!bad) {
            for (var k = 0; k < D.complaints.length; k++) {
              var c = D.complaints[k];
              var ul = root.querySelector('[data-cq="' + c.id + '"]');
              if (!ul) { bad = true; break; }
              var lis = ul.querySelectorAll("[data-qt]");
              if (lis.length !== c.qs.length) { bad = true; break; }
              for (var m = 0; m < c.qs.length; m++) {
                if (norm(lis[m].textContent) !== norm(c.qs[m].text)) { bad = true; break; }
              }
              if (bad) break;
            }
          }
          if (!bad) {
            var vnRows = root.querySelectorAll("#ref-vitals-table tbody tr");
            if (vnRows.length !== D.triageNormals.length) bad = true;
            else for (var p = 0; p < vnRows.length; p++) {
              var vc = vnRows[p].querySelector("[data-vn]");
              if (!vc || norm(vc.textContent) !== norm(D.triageNormals[p].param)) { bad = true; break; }
            }
          }
          if (!bad) {
            var faqs = root.querySelectorAll("#ref-faqs [data-faq-q]");
            if (faqs.length !== D.faqs.length) bad = true;
            else for (var n = 0; n < faqs.length; n++) {
              if (norm(faqs[n].textContent) !== norm(D.faqs[n].q)) { bad = true; break; }
            }
          }
        } catch (e) { bad = true; }
        if (bad) $("aav-integrity").classList.add("bad");
      }

      /* ---------- init ---------- */

      function init() {
        buildSpecies();
        buildRedflags();
        buildComplaints();
        renderCQuestions();
        buildHistory();

        FIELD_IDS.forEach(function (f) {
          var el = $(f.el);
          if (!el) return;
          el.addEventListener("input", function () {
            state.fields[f.key] = el.value;
            if (f.key === "weight") {
              var wv = parseNum(el.value);
              el.setAttribute("aria-invalid", (wv !== null && isNaN(wv)) ? "true" : "false");
            }
            update();
          });
        });

        $("aav-now").addEventListener("click", function () {
          var d = new Date();
          var hh = String(d.getHours()); if (hh.length < 2) hh = "0" + hh;
          var mm = String(d.getMinutes()); if (mm.length < 2) mm = "0" + mm;
          $("aav-arrival").value = hh + ":" + mm;
          state.fields.arrival = hh + ":" + mm;
          update();
        });

        ["temp", "hr", "rr", "sbp", "lact"].forEach(function (v) {
          $("aav-" + v).addEventListener("input", function () {
            state.vitals[v] = this.value;
            renderVitalWarns();
            update();
          });
        });
        ["mm", "crt", "pain", "ment", "pulse"].forEach(function (v) {
          $("aav-" + v).addEventListener("change", function () {
            state.vitals[v] = this.value;
            renderVitalWarns();
            update();
          });
        });

        /* normal-values reference dropdown, rendered from the same data as the flag engine's docs */
        (function () {
          var tb = root.querySelector("#aav-normals-table tbody");
          if (!tb) return;
          var html = "";
          D.triageNormals.forEach(function (r) {
            html += "<tr><td><b>" + esc(r.param) + "</b></td><td>" + esc(r.dog) + "</td><td>" + esc(r.cat) + "</td><td>" + esc(r.note) + "</td></tr>";
          });
          tb.innerHTML = html;
        })();

        $("aav-ovr").addEventListener("change", function () {
          state.override = this.value;
          update();
        });
        $("aav-ovr-reason").addEventListener("input", function () {
          state.ovrReason = this.value;
          update();
        });

        $("aav-print").addEventListener("click", function () { buildPrint(); window.print(); });
        if (window.matchMedia) {
          var mql = window.matchMedia("print");
          var onp = function (e) { if (e.matches) buildPrint(); };
          if (mql && mql.addEventListener) mql.addEventListener("change", onp);
          else if (mql && mql.addListener) mql.addListener(onp);
        }
        window.addEventListener("beforeprint", buildPrint);

        /* keep page content clear of the fixed pinbar on phones */
        function padForPinbar() {
          var bar = $("aav-pinbar");
          var vis = bar && window.getComputedStyle(bar).display !== "none";
          root.style.paddingBottom = vis ? (bar.offsetHeight + 14) + "px" : "";
        }
        window.addEventListener("resize", padForPinbar);
        padForPinbar();

        $("aav-copy").addEventListener("click", function () {
          var txt = buildText();
          var btn = this;
          function done(ok) {
            var old = "Copy as text";
            btn.textContent = ok ? "Copied" : "Copy failed";
            setTimeout(function () { btn.textContent = old; }, 1600);
          }
          function legacyCopy() {
            try {
              var ta = document.createElement("textarea");
              ta.value = txt;
              ta.setAttribute("readonly", "");
              ta.style.position = "absolute";
              ta.style.left = "-9999px";
              document.body.appendChild(ta);
              ta.select();
              var okd = document.execCommand("copy");
              document.body.removeChild(ta);
              done(okd);
            } catch (e) { done(false); }
          }
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(txt).then(function () { done(true); }, legacyCopy);
          } else { legacyCopy(); }
        });

        $("aav-download").addEventListener("click", function () {
          var nm = norm(state.fields.pname) ? norm(state.fields.pname).replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "patient";
          saveTxt("triage-" + nm + ".txt", buildText());
        });

        var newArmed = null;
        $("aav-new").addEventListener("click", function () {
          var btn = this;
          if (newArmed) {
            clearTimeout(newArmed); newArmed = null;
            btn.textContent = "New patient";
            state = { species: "", fields: {}, redflags: {}, complaint: "", answers: {}, vitals: {}, history: {}, override: "", ovrReason: "" };
            try { localStorage.removeItem(LSKEY); } catch (e) {}
            syncSpecies(); syncRedflags(); syncComplaints(); renderCQuestions(); syncHistory();
            FIELD_IDS.forEach(function (f) { var el = $(f.el); if (el) el.value = ""; });
            VKEYS.forEach(function (v) { var el = $("aav-" + v); if (el) el.value = ""; });
            $("aav-ovr").value = ""; $("aav-ovr-reason").value = "";
            renderVitalWarns();
            update();
          } else {
            btn.textContent = "Confirm new patient";
            newArmed = setTimeout(function () { btn.textContent = "New patient"; newArmed = null; }, 3000);
          }
        });

        /* restore */
        var saved = loadSaved();
        if (saved && hasContent(saved.state)) {
          var r = $("aav-restore");
          r.style.display = "block";
          $("aav-restore-yes").addEventListener("click", function () {
            r.style.display = "none";
            applySaved(saved.state);
          });
          $("aav-restore-no").addEventListener("click", function () {
            r.style.display = "none";
            try { localStorage.removeItem(LSKEY); } catch (e) {}
          });
        }

        /* open reference details when a TOC or in-page anchor targets inside it */
        function openTarget() {
          var hash = location.hash;
          if (!hash || hash.length < 2) return;
          var el = root.querySelector(hash);
          if (!el) return;
          var det = el.closest ? el.closest("details") : null;
          if (det) det.open = true;
        }
        window.addEventListener("hashchange", openTarget);
        openTarget();

        renderVitalWarns();
        update();
        integrity();
      }

      init();


  }
}

if (!customElements.get('s-emergency-triage-questions')) {
  customElements.define('s-emergency-triage-questions', EmergencyTriageQuestions);
}
